import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { indexQuerySlice } from '@/shared/components/IndexPage/indexQuerySlice';
import { preferencesSlice } from '@/shared/components/IndexPage/preferencesSlice';
import { navSlice } from '@/app/navigation/navSlice';
import { notificationsSlice } from '@/modules/notifications/notificationsSlice';
import { toastSlice } from '@/shared/toast/toastSlice';
import { listenerMiddleware } from './listeners';
import { loadPersistedState, type PersistedState } from './persistence';

/**
 * Global CLIENT state only. Server data (applications, notifications…) lives in TanStack Query.
 * Add a slice inside its module (modules/<name>/<name>Slice.ts) and register it here.
 */
const rootReducer = combineReducers({
  [indexQuerySlice.reducerPath]: indexQuerySlice.reducer,
  [navSlice.reducerPath]: navSlice.reducer,
  [notificationsSlice.reducerPath]: notificationsSlice.reducer,
  [preferencesSlice.reducerPath]: preferencesSlice.reducer,
  [toastSlice.reducerPath]: toastSlice.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export const makeStore = (persisted?: Partial<PersistedState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState: {
      ...(persisted?.preferences && { preferences: persisted.preferences }),
      ...(persisted?.nav && { nav: { ...navSlice.getInitialState(), ...persisted.nav } }),
    },
    middleware: (getDefault) => getDefault().prepend(listenerMiddleware.middleware),
    devTools: import.meta.env.DEV,
  });
};

export const store = makeStore(loadPersistedState());

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
